import type { Status } from '@googlemaps/react-wrapper'
import type { GoogleServices } from 'components/common/molecules/google-map/google-map'

import Box from '@mui/material/Box'
import { generateAddress } from '@rentspree/helper'
import { useState, useEffect, useContext } from 'react'

import { GoogleMap, MapCustomMarker } from 'components'
import { ListingIdContext } from 'context/listingIdContext'
import { useTracker } from 'store/@hooks/use-tracker'
import { LISTING_PUBLIC_PAGE } from 'tracker/events'

import { EVENT_NAME, MAXIMUM_ZOOM_LEVEL } from './constants'
import styles from './listing-map.module.scss'
import { MarkerInfo } from './marker-info'

const style = {
  width: '100%',
  height: '100%',
}

interface ListingDynamicMapProps extends google.maps.MapOptions {
  position: google.maps.LatLngLiteral
  propertyAddress: PropertyAddress
}

export const ListingDynamicMap: React.FC<ListingDynamicMapProps> = ({
  position,
  zoom,
  propertyAddress,
  ...options
}) => {
  const track = useTracker()
  const listingId = useContext(ListingIdContext)
  const { street, unitNumber } = propertyAddress

  const [map, setMap] = useState<google.maps.Map>() // eslint-disable-line
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>()
  const [googleDistanceMatrixAdaptor, setGoogleDistanceMatrixAdaptor] = useState<IDistanceService>()
  const [clickPropertyName, setClickPropertyName] = useState({})
  const [isFullScreen, setIsFullScreen] = useState(false)

  const postMessage = (message: IframeMessage<null>) => {
    const window = global.window
    window?.parent?.postMessage(message, '*')
  }

  const callback = (status: Status) => {
    postMessage({ eventName: EVENT_NAME[status], payload: null })
  }

  const trackMapInteraction = () =>
    track(LISTING_PUBLIC_PAGE.CLICK_ON_MAP, { listing_id: listingId })

  const handleMapRender = (services: GoogleServices) => {
    if (!map) setMap(services.map)
    if (!googleDistanceMatrixAdaptor) {
      setGoogleDistanceMatrixAdaptor(services.distanceMatrixService)
    }
    if (!placesService) setPlacesService(services.places)
  }

  const smoothZoom = (zoomLevel: number) => {
    const currentZoom = map?.getZoom() as number
    if (currentZoom !== zoomLevel) {
      if (currentZoom < zoomLevel) {
        map?.setOptions({ center: position, zoom: currentZoom + 1 })
      } else {
        map?.setOptions({ center: position, zoom: currentZoom - 1 })
      }
      setTimeout(() => {
        smoothZoom(zoomLevel)
      }, 80)
    }
  }

  useEffect(() => {
    if (map) {
      smoothZoom(MAXIMUM_ZOOM_LEVEL)
    }
  }, [clickPropertyName]) // eslint-disable-line

  const onFullScreen = () => {
    if (!isFullScreen) {
      track(LISTING_PUBLIC_PAGE.CLICK_VIEW_MAP_ON_FULLSCREEN, {
        listing_id: listingId,
      })
      setIsFullScreen(true)
    }
  }

  const onExitFullScreen = () => {
    if (isFullScreen) {
      setIsFullScreen(false)
    }
  }

  const onStreetViewFullScreen = () => {
    track(LISTING_PUBLIC_PAGE.CLICK_VIEW_MAP_ON_FULLSCREEN, {
      listing_id: listingId,
    })
  }

  return (
    <Box data-testid="listing-dynamic-map-wrapper" className={styles.listingMapContentWrapper}>
      <Box data-testid="listing-dynamic-map-content" className={styles.listingMapContent}>
        <GoogleMap
          callback={callback}
          center={position}
          zoom={zoom}
          onClick={trackMapInteraction}
          onDragStart={trackMapInteraction}
          onMapRender={handleMapRender}
          onFullScreen={onFullScreen}
          onExitFullScreen={onExitFullScreen}
          onStreetViewFullScreen={onStreetViewFullScreen}
          mapTypeControl={false}
          streetViewControl={false}
          {...options}
          style={style}
        >
          <MapCustomMarker position={position} onClick={() => setClickPropertyName({})}>
            <MarkerInfo text={generateAddress({ street, unitNumber })} />
          </MapCustomMarker>
        </GoogleMap>
      </Box>
    </Box>
  )
}
